import { useState } from "react";
import { BookOpen, Eye, Feather, Lock, Mail, Mic, RefreshCw, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Bar = ({ label, value, max, icon: Icon, testid }) => (
  <div>
    <div className="mb-2 flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm text-parchment-ivory/80">
        <Icon className="h-4 w-4 text-gold-shimmer" />
        {label}
      </span>
      <span className="font-serif text-xl text-gold-shimmer" data-testid={testid}>{value}</span>
    </div>
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-lake-deep/70">
      <div
        className="h-full rounded-full bg-gradient-to-r from-gold to-gold-shimmer transition-all duration-700"
        style={{ width: `${max > 0 ? Math.max(4, (value / max) * 100) : 0}%` }}
      />
    </div>
  </div>
);

export default function AdminPage() {
  const [pin, setPin] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (code) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/stats", { headers: { "X-Admin-Pin": code } });
      setData(res.data);
    } catch {
      toast.error("Codice non valido.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4" data-testid="admin-gate">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(pin);
          }}
          className="card-parchment w-full max-w-sm rounded-xl p-8 text-center"
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-lake-deep">
            <Lock className="h-5 w-5 text-gold-shimmer" />
          </div>
          <h1 className="mt-5 font-serif text-2xl text-ink-dark">Area riservata</h1>
          <p className="mt-2 text-sm text-ink-muted">Il cruscotto del lago: solo per chi conosce il segreto.</p>
          <input
            data-testid="admin-pin-input"
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Codice di accesso"
            className="mt-6 w-full rounded-lg border border-ink-dark/15 bg-white/60 px-4 py-3 text-sm text-ink-dark placeholder:text-ink-muted/60 focus:border-gold focus:outline-none"
          />
          <button
            data-testid="admin-pin-submit"
            type="submit"
            disabled={loading}
            className="mt-4 w-full rounded-full bg-lake-deep px-6 py-3 text-sm font-semibold text-parchment-ivory transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          >
            {loading ? "Verifica…" : "Entra"}
          </button>
          <a href="/" className="mt-5 inline-block text-xs text-ink-muted underline decoration-gold/50 underline-offset-4 hover:text-ink-dark">
            ← Torna al sito
          </a>
        </form>
      </div>
    );
  }

  const ebook = data.interesse.ebook;
  const podcast = data.interesse.podcast;
  const maxVoti = Math.max(ebook, podcast);

  return (
    <div className="min-h-screen px-4 py-16 sm:px-6" data-testid="admin-dashboard">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Feather className="h-6 w-6 text-gold" />
            <div>
              <h1 className="font-serif text-2xl text-parchment-ivory">Il cruscotto del lago</h1>
              <p className="text-xs uppercase tracking-[0.25em] text-gold/70">Il Segreto del Lago — La Saga</p>
            </div>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={() => load(pin)}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs font-semibold text-gold-shimmer transition-colors hover:bg-gold/10"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Aggiorna
          </button>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="rounded-xl border border-gold/25 bg-lake-teal/30 p-6" data-testid="admin-votes-card">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Domanda formati futuri</p>
            <h2 className="mt-2 font-serif text-xl text-parchment-ivory">Ebook o podcast?</h2>
            <div className="mt-6 space-y-5">
              <Bar label="Versione digitale (ebook)" value={ebook} max={maxVoti} icon={BookOpen} testid="admin-votes-ebook" />
              <Bar label="Podcast delle leggende" value={podcast} max={maxVoti} icon={Mic} testid="admin-votes-podcast" />
            </div>
            <p className="mt-5 text-xs text-parchment-ivory/45">
              Totale voti: <span className="font-semibold text-parchment-ivory" data-testid="admin-votes-total">{ebook + podcast}</span>
            </p>
          </div>

          <div className="rounded-xl border border-gold/25 bg-lake-teal/30 p-6" data-testid="admin-newsletter-card">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Diario di Bordo</p>
            <div className="mt-2 flex items-baseline gap-3">
              <span className="font-serif text-5xl text-gold-shimmer" data-testid="admin-newsletter-total">
                {data.newsletter_totale}
              </span>
              <span className="text-sm text-parchment-ivory/60">iscritti alla newsletter</span>
            </div>
            <div className="mt-5 max-h-56 space-y-2 overflow-y-auto pr-1" data-lenis-prevent>
              {data.newsletter_ultimi.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 rounded-lg bg-lake-deep/50 px-3 py-2">
                  <span className="truncate text-xs text-parchment-ivory/80">
                    <Mail className="mr-1.5 inline h-3 w-3 text-gold-shimmer" />
                    {s.email}
                  </span>
                  <span className="shrink-0 text-[10px] text-parchment-ivory/40">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString("it-IT") : ""}
                  </span>
                </div>
              ))}
              {data.newsletter_ultimi.length === 0 && (
                <p className="text-xs text-parchment-ivory/40">Nessun iscritto ancora.</p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gold/25 bg-lake-teal/30 p-6 md:col-span-2" data-testid="admin-visits-card">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Visite al sito</p>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div>
                <p className="font-serif text-4xl text-gold-shimmer" data-testid="admin-visits-today">{data.visite_oggi}</p>
                <p className="mt-1 text-xs text-parchment-ivory/60">oggi</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-gold-shimmer" data-testid="admin-visits-week">{data.visite_7gg}</p>
                <p className="mt-1 text-xs text-parchment-ivory/60">ultimi 7 giorni</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-gold-shimmer" data-testid="admin-visits-total">{data.visite_totale}</p>
                <p className="mt-1 text-xs text-parchment-ivory/60">totali</p>
              </div>
            </div>
            <p className="mt-4 flex items-center gap-2 text-xs text-parchment-ivory/45">
              <Eye className="h-3.5 w-3.5 text-gold-shimmer" />
              Conteggio semplice e anonimo: una visita per ogni apertura di pagina.
            </p>
          </div>

          <div className="rounded-xl border border-gold/25 bg-lake-teal/30 p-6 md:col-span-2" data-testid="admin-contacts-card">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Richieste dal modulo contatti</p>
            <div className="mt-2 flex items-baseline gap-3">
              <Users className="h-5 w-5 translate-y-1 text-gold-shimmer" />
              <span className="font-serif text-4xl text-gold-shimmer" data-testid="admin-contacts-total">
                {data.contatti_totale}
              </span>
              <span className="text-sm text-parchment-ivory/60">richieste ricevute</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(data.contatti_per_tipo).map(([tipo, n]) => (
                <span key={tipo} className="rounded-full border border-gold/30 px-3 py-1.5 text-xs text-parchment-ivory/75">
                  {tipo}: <span className="font-semibold text-gold-shimmer">{n}</span>
                </span>
              ))}
              {Object.keys(data.contatti_per_tipo).length === 0 && (
                <p className="text-xs text-parchment-ivory/40">Nessuna richiesta ancora.</p>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-gold/25 bg-lake-teal/30 p-6 md:col-span-2" data-testid="admin-quiz-card">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Quiz &laquo;Quale personaggio sei?&raquo;</p>
            <div className="mt-2 flex items-baseline gap-3">
              <Sparkles className="h-5 w-5 translate-y-1 text-gold-shimmer" />
              <span className="font-serif text-4xl text-gold-shimmer" data-testid="admin-quiz-total">
                {data.quiz_totale}
              </span>
              <span className="text-sm text-parchment-ivory/60">quiz completati (email raccolte per il gadget)</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(data.quiz_per_personaggio).map(([p, n]) => (
                <span key={p} className="rounded-full border border-gold/30 px-3 py-1.5 text-xs text-parchment-ivory/75">
                  {p}: <span className="font-semibold text-gold-shimmer">{n}</span>
                </span>
              ))}
              {Object.keys(data.quiz_per_personaggio).length === 0 && (
                <p className="text-xs text-parchment-ivory/40">Nessun quiz completato ancora.</p>
              )}
            </div>
          </div>
        </div>

        <a href="/" className="mt-10 inline-block text-xs text-parchment-ivory/40 underline decoration-gold/40 underline-offset-4 hover:text-gold-shimmer">
          ← Torna al sito
        </a>
      </div>
    </div>
  );
}
